// src/screens/Marketplace/EscrowListScreen.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ReviewPopup from '../../components/popups/ReviewPopup';

import api from '../../api';
import './EscrowListScreen.css';
import backArrowIcon from '../../assets/backarrow-icon.png';
import tickIcon from '../../assets/tick-icon.png';
import infoIcon from '../../assets/info-icon.png';
import ReadMoreText from '../../common/ReadMoreText';

import CannotConfirmDisputedOrderDrawer from '../../components/popups/CannotConfirmDisputedOrderDrawer';
import DisputeAlreadyRaisedDrawer from '../../components/popups/DisputeAlreadyRaisedDrawer';
import BuyerConfirmDrawer from '../../components/popups/BuyerConfirmDrawer';
import PauseDrawer from '../../components/popups/PauseDrawer';

function EscrowListScreen({ user }) {
  const navigate = useNavigate();
  const { orderId } = useParams();

  const [order, setOrder] = useState(null);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [showTxnInfo, setShowTxnInfo] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState('');
  const [sellerTimeLeft, setSellerTimeLeft] = useState('');
  const scrollRef = useRef();
  const location = useLocation();
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const hasReviewed = order?.hasReviewed ?? false;
  const [newMessage, setNewMessage] = useState(false);

  const [showBuyerConfirmDrawer, setShowBuyerConfirmDrawer] = useState(false);
  const [showCannotConfirmDrawer, setShowCannotConfirmDrawer] = useState(false);
  const [showAlreadyDisputedDrawer, setShowAlreadyDisputedDrawer] = useState(false);

  const [showPauseDrawer, setShowPauseDrawer] = useState(false);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || !el.children.length) return;

    const scrollLeft = el.scrollLeft;
    const containerWidth = el.offsetWidth;
    const index = Math.round(scrollLeft / containerWidth);

    setCurrentImageIndex(index);
  };

  // Fetch order
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await api.get(`/escrow/${orderId}`);
        setOrder(res.data);
        console.log('🧾 Order fetched:', res.data);
        console.log('⚠️ isDisputed:', res.data.isDisputed);
      } catch (err) {
        console.error('❌ Failed to load order:', err);
      }
    };
    fetchOrder();
  }, [orderId]);

  // Check for new chat messages related to this order parties
  useEffect(() => {
    const checkForNewMessages = async () => {
      try {
        const res = await api.get(`/messages/chats/${user.username}`);
        const unreadMessages = res.data.filter(
          (msg) => msg.unread > 0 && (msg.otherUsername === order?.buyer || msg.otherUsername === order?.seller)
        );
        setNewMessage(unreadMessages.length > 0);
      } catch (err) {
        console.error('❌ Failed to fetch messages:', err);
      }
    };

    if (order?.status === 'pending') {
      checkForNewMessages();
    }

    const intervalId = setInterval(checkForNewMessages, 30000);
    return () => clearInterval(intervalId);
  }, [orderId, order?.status, user.username]);

  // Buyer auto-release countdown
  useEffect(() => {
    if (!order?.autoReleaseDate || order.status !== 'pending') return;

    const updateCountdown = () => {
      if (order.disputePaused) {
        setTimeLeft(<span className="paused-clickable" onClick={() => setShowPauseDrawer(true)}>⏸️ Paused</span>);
        return;
      }

      const now = new Date();
      const release = new Date(order.autoReleaseDate);
      const diff = release - now;

      if (diff <= 0) {
        setTimeLeft('Auto-releasing...');
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      setTimeLeft(`${days}d ${hours}h ${minutes}m`);
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 60 * 1000);
    return () => clearInterval(timer);
  }, [order]);

  // Seller must confirm within 48h
  useEffect(() => {
    if (!order || order.status !== 'pending' || order.sellerConfirmed) return;

    const createdAt = new Date(order.createdAt);
    const deadline = new Date(createdAt.getTime() + 48 * 60 * 60 * 1000); // 48h

    const updateSellerCountdown = () => {
      const now = new Date();
      const diff = deadline - now;

      if (diff <= 0) {
        setSellerTimeLeft('Expired');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      setSellerTimeLeft(`${hours}h ${minutes}m`);
    };

    updateSellerCountdown();
    const timer = setInterval(updateSellerCountdown, 60 * 1000);
    return () => clearInterval(timer);
  }, [order]);

  const isBuyer = user.username === order?.buyer;
  const sellerExpired =
    !isBuyer &&
    !order?.sellerConfirmed &&
    order?.status === 'pending' &&
    sellerTimeLeft === 'Expired';

  const isExpired = order?.expiresAt && new Date(order.expiresAt) < new Date();
  const otherParty = isBuyer ? order?.seller : order?.buyer;

  const handleConfirm = async () => {
    try {
      await api.patch(`/escrow/${orderId}/status`, {
        role: isBuyer ? 'buyer' : 'seller',
        username: user.username,
      });
      const updated = await api.get(`/escrow/${orderId}`);
      setOrder(updated.data);

      if (isBuyer) {
        setTimeout(() => setShowReviewPopup(true), 500);
      }
    } catch (err) {
      console.error('❌ Confirm failed:', err);
      alert('Confirmation failed.');
    }
  };

  const handleCancel = async () => {
    try {
      await api.patch(`/escrow/${orderId}/cancel`, { username: user.username });
    } catch (err) {
      console.error('❌ Cancel failed:', err);
      alert('Failed to cancel order.');
    } finally {
      const updated = await api.get(`/escrow/${orderId}`);
      setOrder(updated.data);
    }
  };

const isDisputed = order?.isDisputed;

// Dispute flags (works with multiple backend shapes)
const disputeDecision = order?.disputeDecision || null;               // e.g., { outcome: 'buyer'|'seller', decidedAt, ... }
const disputeStatus = (order?.disputeStatus || '').toLowerCase();     // optional: 'open' | 'resolved'

// Treat admin resolution as resolved even if backend didn't set disputeResolved/decision yet.
// (Your flow: completed = seller wins, cancelled = buyer wins)
const statusStr = (order?.status || '').toLowerCase();
const isResolvedByStatus =
  order?.isDisputed === true && (statusStr === 'completed' || statusStr === 'cancelled');

const isDisputeResolved =
  !!disputeDecision ||
  disputeStatus === 'resolved' ||
  order?.disputeResolved === true ||
  isResolvedByStatus;

// IMPORTANT: Only "open" if NOT resolved.
const isDisputeOpen =
  !isDisputeResolved && (order?.isDisputed === true || disputeStatus === 'open');


  if (!order) return <p>Loading order details...</p>;

  // ========= 🔒 SNAPSHOT-FIRST HELPERS =========

  const nameAtPurchase =
    order?.productNameAtPurchase || order?.productId?.name || '';

  const descriptionAtPurchase =
    order?.productDescriptionAtPurchase || order?.productId?.description || '';

  const conditionFromSnapshot = order?.productConditionAtPurchase; // 'new' | 'used' | ''
  const conditionLive = order?.productId?.condition;
  const conditionLabel =
    (conditionFromSnapshot === 'used' || conditionLive === 'used') ? 'Used' :
    (conditionFromSnapshot === 'new'  || conditionLive === 'new')  ? 'New'  :
    null;

  // Use fixed ETA stored on escrow; fallback for legacy
  const estimatedDays =
    typeof order?.expectedDeliveryDays === 'number'
      ? order.expectedDeliveryDays
      : (order?.productId?.expectedDeliveryDays ?? null);

  // Delivery list: prefer snapshot
  const deliveryMethods = Array.isArray(order?.deliveryMethodsAtPurchase) && order.deliveryMethodsAtPurchase.length
    ? order.deliveryMethodsAtPurchase
    : (
        Array.isArray(order?.productId?.deliveryMethod)
          ? order.productId.deliveryMethod
          : (typeof order?.productId?.deliveryMethod === 'string' && order.productId.deliveryMethod.trim())
            ? [order.productId.deliveryMethod.trim()]
            : []
      );

  // Images: prefer snapshot
  const mediaAtPurchase =
    (Array.isArray(order?.productMediaAtPurchase) && order.productMediaAtPurchase.length)
      ? order.productMediaAtPurchase
      : (order?.productId?.media || []);

  // ========= /SNAPSHOT-FIRST HELPERS =========

  return (
    <div className="escrowlist-container">
      <div className="escrowlist-header">
        <img
          src={backArrowIcon}
          alt="Back"
          className="escrowlist-back-icon"
          onClick={() =>
            navigate('/orders', {
              state: { fromFilter: location.state?.fromFilter || 'all' },
            })
          }
        />
        <h2>Order Details</h2>
      </div>

      <div className="escrowlist-scroll">
        <div className="escrowlist-card">
          {/* Images */}
          <div className="escrow-carousel-wrapper">
            <div className="escrow-carousel-scroll" ref={scrollRef} onScroll={handleScroll}>
              {mediaAtPurchase.map((url, i) => (
                <img key={i} src={url} alt={`escrow-${i}`} className="escrow-carousel-img" />
              ))}
            </div>

            {mediaAtPurchase.length > 1 && (
              <div className="escrow-carousel-dots">
                {mediaAtPurchase.map((_, index) => (
                  <span
                    key={index}
                    className={`escrow-dot ${currentImageIndex === index ? 'active' : ''}`}
                  />
                ))}
              </div>
            )}
          </div>



<div className="escrowlist-info">
  {/* Title */}
  <div className="escrowlist-block">
    <span className="escrowlist-block-label">Title:</span>
    <span className="escrowlist-block-value">{nameAtPurchase}</span>
  </div>

  {/* Description */}
  <div className="escrowlist-block">
    <span className="escrowlist-block-label">Description:</span>
    <span className="escrowlist-block-value">
      <ReadMoreText text={descriptionAtPurchase} maxLines={3} />
    </span>
  </div>

{/* Condition */}
{conditionLabel && (
  <div className="escrowlist-block">
    <span className="escrowlist-block-label">Condition:</span>
    <div className="escrowlist-block-value">
      <span
        className={`pd-badge ${
          (conditionFromSnapshot || conditionLive) === 'used' ? 'used' : 'new'
        }`}
      >
        {conditionLabel}
      </span>
    </div>
  </div>
)}

{/* Delivery Methods */}
{deliveryMethods.length > 0 && (
  <div className="escrowlist-block">
    <span className="escrowlist-block-label">
      Delivery Method{deliveryMethods.length > 1 ? 's' : ''}:
    </span>
    <div className="escrowlist-block-value" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {deliveryMethods.map((m, i) => (
        <span key={`${m}-${i}`} className="pd-badge">
          {m}
        </span>
      ))}
    </div>
  </div>
)}

  {/* Estimated Delivery */}
  {typeof estimatedDays === 'number' && estimatedDays >= 0 && (
    <div className="escrowlist-block">
      <span className="escrowlist-block-label">Estimated Delivery:</span>
      <span className="escrowlist-block-value">
        {estimatedDays} day{estimatedDays === 1 ? '' : 's'}
      </span>
    </div>
  )}

  {/* Amount */}
  {order.amount && (
    <div className="escrowlist-block">
      <span className="escrowlist-block-label">Amount:</span>
      <span className="escrowlist-block-value amount">{order.amount} Pi</span>
    </div>
  )}

  {/* Order ID (keep) */}
  <div className="escrowlist-block">
    <span className="escrowlist-block-label">Order ID:</span>
    <span
      className="escrowlist-block-value"
      onClick={() => {
        navigator.clipboard.writeText(order._id);
        alert('Order ID copied!');
      }}
      style={{ color: '#007bff', cursor: 'pointer' }}
    >
      {order._id}
    </span>
  </div>


  {/* Buyer delivery details (optional) */}
  {order.buyerDeliveryAddress && (
    <div className="escrowlist-block">
      <span className="escrowlist-block-label">Buyer’s Delivery Address:</span>
      <span className="escrowlist-block-value">
        <ReadMoreText text={order.buyerDeliveryAddress} maxLines={3} />
      </span>
    </div>
  )}

  {(order.buyerNote || order.message) && (
    <div className="escrowlist-block">
      <span className="escrowlist-block-label">Buyer’s Optional Message:</span>
      <span className="escrowlist-block-value">
        <ReadMoreText text={order.buyerNote || order.message} maxLines={3} />
      </span>
    </div>
  )}




            <div className="escrowlist-actions-row">
              <span className="seller-username">@{otherParty}</span>
              <button className="chat-btn" onClick={() => navigate(`/chat/${otherParty}`)}>
                Chat
                {newMessage && <span className="new-message-dot"></span>}
              </button>
            </div>


            <p className={`escrowlist-status ${order.status}`}>
              {order.status}
            </p>


{/* Dispute status quick link (open) */}
{isDisputeOpen && (
  <p
    className="disputed-status-label clickable"
    onClick={(e) => {
      e.stopPropagation();
      navigate(`/dispute-decision/${order._id}`, { state: { fromOrder: true } });
    }}
  >
    ⚠️ This order is under dispute (View)
  </p>
)}

{/* Dispute status quick link (resolved) */}
{isDisputeResolved && (
  <p
    className="disputed-status-label clickable"
    onClick={(e) => {
      e.stopPropagation();
      navigate(`/dispute-decision/${order._id}`, { state: { fromOrder: true, resolved: true } });
    }}
  >
     Dispute of this order has been resolved (View)
  </p>
)}



            {order.status === 'completed' && order.transactionId && (
              <div className="txn-id-row">
                <span className="txn-id-label">TXN ID:</span>
                <span
                  className="txn-id-value"
                  onClick={() => {
                    navigator.clipboard.writeText(order.transactionId);
                    alert('Transaction ID copied!');
                  }}
                >
                  {order.transactionId}
                </span>
                <img
                  src={infoIcon}
                  alt="info"
                  className="info-icon"
                  onClick={() => setShowTxnInfo(true)}
                />
              </div>
            )}

            {order.status === 'completed' && isBuyer && !hasReviewed && (
              <button
                className="escrow-review-button"
                onClick={() => setShowReviewPopup(true)}
              >
                ✍️ Add Review
              </button>
            )}

            {order.status === 'pending' && (
              <>
                {(isBuyer && order.buyerConfirmed) || (!isBuyer && order.sellerConfirmed) ? (
                  <p className="status-msg">
                    <img src={tickIcon} alt="Tick" className="tick-icon" />
                    You’ve confirmed. Waiting for other party...
                  </p>
                ) : null}

                {(isBuyer && order.sellerConfirmed && !order.buyerConfirmed) ||
                (!isBuyer && order.buyerConfirmed && !order.sellerConfirmed) ? (
                  <p className="status-msg secondary">
                    🕒 Other party has confirmed. Waiting for you...
                  </p>
                ) : null}

                {!order.sellerConfirmed && isBuyer && (
                  <p className="status-msg secondary">
                    {sellerTimeLeft === 'Expired' ? (
                      <>Seller failed to confirm in time. Refund on the way.</>
                    ) : (
                      <>Seller has <span className="countdown-highlight">{sellerTimeLeft}</span> to confirm.</>
                    )}
                  </p>
                )}

                {!order.sellerConfirmed && !isBuyer && (
                  <p className="status-msg secondary">
                    {sellerTimeLeft === 'Expired'
                      ? 'You have failed to confirm this order in time.'
                      : <>You have <span className="countdown-highlight">{sellerTimeLeft}</span> to confirm.</>}
                  </p>
                )}

                {order.sellerConfirmed && !order.buyerConfirmed && (
                  <p className="status-msg secondary">
                    {timeLeft === 'Auto-releasing...' ? (
                      isBuyer ? (
                        <>You failed to confirm or raise dispute in time. Pi is being released to the seller.</>
                      ) : (
                        <>Buyer didn’t confirm or raise dispute in time. Pi is being released to you....</>
                      )
                    ) : (
                      isBuyer ? (
                        <>⏳ Auto-release in: <span className="countdown-highlight">{timeLeft}</span> — confirm or dispute soon.</>
                      ) : (
                        <>⏳ Buyer must confirm or dispute in: <span className="countdown-highlight">{timeLeft}</span></>
                      )
                    )}
                  </p>
                )}

                {isBuyer && order.sellerConfirmed && (
                  <>
                    <div className="confirm-label-row">
                      <span className="confirm-label-text">Confirm Order</span>
                      <img
                        src={infoIcon}
                        alt="Info"
                        className="info-icon"
                        onClick={() => setShowInfoPopup(true)}
                      />
                    </div>

                    {!order.buyerConfirmed && timeLeft !== 'Auto-releasing...' && (
                      <div className="status-actions side-by-side">
                        {isDisputed ? (
                          <button
                            className="confirm-btn faded"
                            onClick={() => setShowCannotConfirmDrawer(true)}
                          >
                            Confirm Order
                          </button>
                        ) : (
                          <button
                            className="confirm-btn"
                            onClick={() => setShowBuyerConfirmDrawer(true)}
                          >
                            Confirm Order
                          </button>
                        )}

                        {isDisputed ? (
                          <button
                            className="dispute-btn faded"
                            onClick={() => setShowAlreadyDisputedDrawer(true)}
                          >
                            Raise Dispute
                          </button>
                        ) : (
                          <button
                            className="dispute-btn"
                            onClick={() => navigate(`/dispute/${order._id}`)}
                          >
                            Raise Dispute
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}

                {!isBuyer && !order.sellerConfirmed && !sellerExpired && (
                  <div className="status-actions">
                    <button className="confirm-btn" onClick={handleConfirm}>Confirm Order</button>
                    <button className="cancel-btn" onClick={handleCancel}>Cancel</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {showInfoPopup && (
        <div className="popup-overlay" onClick={() => setShowInfoPopup(false)}>
          <div className="popup-box" onClick={(e) => e.stopPropagation()}>
            <h3>⚠️ Important</h3>
            <p>
              BarterFi uses an escrow system. Only confirm after receiving the product/service.
              Once both sides confirm, the transaction is finalized.
            </p>
            <button onClick={() => setShowInfoPopup(false)}>Got it</button>
          </div>
        </div>
      )}

      {showTxnInfo && (
        <div className="popup-overlay" onClick={() => setShowTxnInfo(false)}>
          <div className="popup-box" onClick={(e) => e.stopPropagation()}>
            <h3>🔒 TXN ID Info</h3>
            <p>This is your public Pi transaction ID. You can use it to link a post or prove payment.</p>
            <button onClick={() => setShowTxnInfo(false)}>Got it</button>
          </div>
        </div>
      )}

      {showBuyerConfirmDrawer && (
        <BuyerConfirmDrawer
          onClose={() => setShowBuyerConfirmDrawer(false)}
          onConfirm={() => {
            setShowBuyerConfirmDrawer(false);
            handleConfirm();
          }}
        />
      )}

      {showCannotConfirmDrawer && (
        <CannotConfirmDisputedOrderDrawer onClose={() => setShowCannotConfirmDrawer(false)} />
      )}

      {showAlreadyDisputedDrawer && (
        <DisputeAlreadyRaisedDrawer onClose={() => setShowAlreadyDisputedDrawer(false)} />
      )}

      {showReviewPopup && (
        <ReviewPopup
          productId={order.productId?._id}
          buyer={user.username}
          onClose={() => setShowReviewPopup(false)}
        />
      )}

      {showPauseDrawer && <PauseDrawer onClose={() => setShowPauseDrawer(false)} />}
    </div>
  );
}

export default EscrowListScreen;
